<template>
  <div class="rounded border">
    <slot name="media" />

    <div
      class="whitespace-pre-wrap break-words p-8"
      :class="{ 'rtl text-right': ['ar', 'he'].includes(language) }"
    >
      <div
        v-if="currentIndex === 0 && initialText && partnerName"
        class="mb-4 text-sm font-semibold"
      >
        {{ $t("ai_suggested_by").replace(":partner", partnerName) }}
      </div>
      <span
        v-if="error"
        class="flex items-center space-x-2.5 text-red-600"
      >
        <Icon icon="times" />
        <span>{{ $t("ai_error") }}</span>
      </span>
      <span
        v-else-if="!currentDraft.text"
        class="block"
      >
        <Icon
          icon="spinner-third"
          prefix="fad"
          spin
        />
      </span>
      <span v-else>{{ currentDraft.text }}</span>
    </div>

    <div class="flex divide-x border-t text-sm">
      <div class="flex shrink-0 items-center text-supplement">
        <button
          class="py-4 pl-5 pr-4"
          :class="currentIndex === 0 ? 'cursor-not-allowed text-gray-300' : `hover:text-paragraph`"
          @click="previousDraft"
        >
          <Icon icon="chevron-left" />
        </button>
        <span>{{ currentIndex + 1 }} / {{ drafts.length }}</span>
        <button
          class="py-4 pl-4 pr-5"
          :class="currentIndex === lastIndex ? 'cursor-not-allowed text-gray-300' : 'hover:text-paragraph'"
          @click="nextDraft"
        >
          <Icon icon="chevron-right" />
        </button>
      </div>
      <div class="flex grow items-center justify-end">
        <div
          v-if="status === 'pending'"
          class="px-5 py-4"
        >
          <Icon
            icon="spinner-third"
            prefix="fad"
            spin
          />
        </div>
        <button
          v-else
          class="space-x-3 p-5 py-4"
          @click="regenerate"
        >
          <span>{{ $t("ai_regenerate") }}</span>
          <Icon
            icon="refresh"
            prefix="far"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useStream } from '@laravel/stream-vue'
import type { AiGenerationStatus } from '~/types/ai'

type AiDraftProps = {
  modelValue: string | null | undefined
  endpoint: string
  language?: string
  partnerName?: string
  initialText?: string
  body?: Record<string, unknown>
  replacements?: Record<string, string>
}

const props = withDefaults(defineProps<AiDraftProps>(), {
  language: 'en'
})

const emit = defineEmits(['update:modelValue', 'status'])

const { backendUrl } = useRuntimeConfig().public
const url = computed(() => `${backendUrl}/${props.endpoint}`)

const status = ref<AiGenerationStatus>('idle')
const error = ref(false)
const csrfToken = ref('')

const drafts = ref<{ text: string }[]>([{ text: props.initialText ?? '' }])
const currentIndex = ref(0)
const lastIndex = computed(() => drafts.value.length - 1)
const currentDraft = computed(() => drafts.value[currentIndex.value])
// The draft a generation is writing into, captured at start so navigation can't redirect it.
const streamingDraft = ref<{ text: string } | null>(null)

if (props.initialText) {
  status.value = 'success'
}

const { data, send, clearData } = useStream(url, {
  credentials: 'include',
  onBeforeSend: (request) => {
    request.headers = {
      ...request.headers,
      'X-XSRF-TOKEN': csrfToken.value
    }
  },
  onFinish: () => {
    // A 200 with no content means generation failed (e.g. the provider returned an error event).
    if (data.value.trim() === '') {
      error.value = true
      status.value = 'error'
    } else {
      status.value = 'success'
    }
  },
  onError: () => {
    error.value = true
    status.value = 'error'
  }
})

// Generate a fresh draft into the active entry. Any drafts already produced are
// sent so the API can ask for a genuinely different variation, not a reword.
const runStream = async () => {
  error.value = false
  status.value = 'pending'
  streamingDraft.value = drafts.value[currentIndex.value]

  const previous = drafts.value.filter(draft => draft !== streamingDraft.value && draft.text.trim() !== '').map(draft => draft.text)

  try {
    csrfToken.value = (await initCsrf(true)) ?? ''
    clearData()
    send({ ...props.body, previous })
  } catch {
    error.value = true
    status.value = 'error'
  }
}

// Pipe streamed text into the draft it was started for, applying replacements (e.g. [link]).
const unwatchData = watch(data, () => {
  if (!streamingDraft.value) {
    return
  }

  let text = data.value

  if (props.replacements) {
    Object.entries(props.replacements).forEach(([key, value]) => {
      text = text.replaceAll(key, value)
    })
  }

  streamingDraft.value.text = text

  if (streamingDraft.value === currentDraft.value) {
    emit('update:modelValue', text)
  }
})
onUnmounted(unwatchData)

const unwatchStatus = watch(status, () => emit('status', status.value))
onUnmounted(unwatchStatus)

onMounted(() => {
  if (!props.initialText) {
    runStream()
  }
})

// Regenerate
const regenerate = () => {
  drafts.value.push({ text: '' })
  currentIndex.value = drafts.value.length - 1
  runStream()
}

// History navigation
const previousDraft = () => {
  if (currentIndex.value > 0) {
    --currentIndex.value
    emit('update:modelValue', currentDraft.value.text)
  }
}

const nextDraft = () => {
  if (currentIndex.value < lastIndex.value) {
    ++currentIndex.value
    emit('update:modelValue', currentDraft.value.text)
  }
}
</script>
